// Client Portal — Podcast Studio portal. Extracted from App.js.
import { useState, useEffect, useRef } from "react";
import {
  AlertTriangle,
  BarChart3,
  Check,
  CheckCircle,
  Edit3,
  FileText,
  Headphones,
  Loader2,
  Mic,
  Play,
  Plus,
  Radio,
  RefreshCw,
  RotateCcw,
  Share2,
  Sparkles,
  Trash2,
  Upload,
  Users,
  Volume2,
} from "lucide-react";
import { authFetch, authUpload, getToken, AUTH_URL } from "../lib/api";
import { showToast } from "../lib/toast";
import { SocialShareDialog } from "../components/SocialShareDialog";
import { WizardShell } from "../components/WizardShell";

import {
  podcastErr,
  fetchPodcastAudioUrl,
  podcastInputCls,
  PodcastField,
  PodcastModal,
  PodcastAudioPlayer,
  PODCAST_STYLES_FALLBACK,
  PODCAST_BUSY_STATUSES,
  PODCAST_EDITABLE_STATUSES,
  PODCAST_STATUS_STYLES,
  PODCAST_STATUS_LABELS,
  PODCAST_PROVIDERS,
} from "../components/podcastUi";

// Absolute public URL for a published episode's audio (feed capability token).
const episodePublicAudioUrl = (feedToken, episodeId) => {
  const base = AUTH_URL.startsWith("http")
    ? AUTH_URL
    : `${window.location.origin}${AUTH_URL}`;
  return `${base}/podcast/feed/${feedToken}/episodes/${episodeId}.mp3`;
};

const PodcastVoiceForm = ({
  facilityId,
  ttsStyles,
  initial,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState({
    display_name: initial?.display_name || "",
    provider: initial?.provider || "azure",
    provider_voice_id: initial?.provider_voice_id || "",
    gender: initial?.gender || "",
    default_style: initial?.default_style || "",
    is_cloned: initial?.is_cloned || false,
    clone_consent_doc: initial?.clone_consent_doc || "",
    clone_consent_signer: initial?.clone_consent_signer || "",
    clone_consent_date: initial?.clone_consent_date
      ? String(initial.clone_consent_date).slice(0, 10)
      : "",
    clone_consent_notes: initial?.clone_consent_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [providerVoices, setProviderVoices] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    setProviderVoices([]);
    setPreviewUrl(null);
    authFetch(`/podcast/provider-voices?provider=${form.provider}`).then(
      (data) => {
        if (!data.error) setProviderVoices(data.voices || []);
      },
    );
  }, [form.provider]);

  useEffect(
    () => () => previewUrl && URL.revokeObjectURL(previewUrl),
    [previewUrl],
  );

  const previewVoice = async () => {
    if (!form.provider_voice_id.trim()) {
      showToast("Pick a provider voice first.");
      return;
    }
    setPreviewing(true);
    setPreviewUrl(null);
    try {
      const res = await fetch(`${AUTH_URL}/podcast/voices/preview`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          facility_id: facilityId,
          provider: form.provider,
          provider_voice_id: form.provider_voice_id.trim(),
          style: form.default_style || null,
        }),
      });
      if (!res.ok) {
        let msg = "Voice preview failed.";
        try {
          const j = await res.json();
          msg = j.message || j.error || msg;
        } catch {}
        showToast(msg);
        return;
      }
      setPreviewUrl(URL.createObjectURL(await res.blob()));
    } catch {
      showToast("Voice preview failed.");
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    if (!form.display_name.trim() || !form.provider_voice_id.trim()) {
      showToast("Name and provider voice ID are required.");
      return;
    }
    if (
      form.is_cloned &&
      (!form.clone_consent_doc.trim() ||
        !form.clone_consent_signer.trim() ||
        !form.clone_consent_date)
    ) {
      showToast(
        "Cloned voices require a consent reference, signer name, and consent date.",
      );
      return;
    }
    setSaving(true);
    const body = JSON.stringify({
      ...form,
      facility_id: facilityId,
      clone_consent_doc: form.clone_consent_doc.trim() || null,
    });
    const data = initial?.id
      ? await authFetch(`/podcast/voices/${initial.id}`, {
          method: "PUT",
          body,
        })
      : await authFetch(`/podcast/voices`, { method: "POST", body });
    setSaving(false);
    const err = podcastErr(data, "Failed to save voice.");
    if (err) {
      showToast(err);
      return;
    }
    showToast(initial?.id ? "Voice updated." : "Voice created.", "success");
    onSaved();
  };

  return (
    <PodcastModal
      title={initial?.id ? "Edit Voice" : "New Voice"}
      onClose={onClose}
    >
      <div className="space-y-4">
        <PodcastField label="Display name" required>
          <input
            className={podcastInputCls}
            value={form.display_name}
            onChange={(e) => set("display_name", e.target.value)}
            placeholder="e.g. Sarah — Warm Host"
          />
        </PodcastField>
        <PodcastField label="Provider" required>
          <select
            className={podcastInputCls}
            value={form.provider}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                provider: e.target.value,
                provider_voice_id: "",
                gender: "",
              }))
            }
          >
            {PODCAST_PROVIDERS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </PodcastField>
        <PodcastField label="Provider voice ID" required>
          {providerVoices.length > 0 ? (
            <select
              className={podcastInputCls}
              value={form.provider_voice_id}
              onChange={(e) => {
                const selected = providerVoices.find(
                  (v) => v.voice_id === e.target.value,
                );
                setForm((p) => ({
                  ...p,
                  provider_voice_id: e.target.value,
                  gender: selected?.gender?.toLowerCase() || p.gender,
                }));
              }}
            >
              <option value="">— select a voice —</option>
              {providerVoices.map((v) => (
                <option key={v.voice_id} value={v.voice_id}>
                  {v.display_name} ({v.gender})
                </option>
              ))}
            </select>
          ) : (
            <input
              className={podcastInputCls}
              value={form.provider_voice_id}
              onChange={(e) => set("provider_voice_id", e.target.value)}
              placeholder="e.g. voice ID from provider"
            />
          )}
        </PodcastField>
        <PodcastField label="Gender">
          <select
            className={podcastInputCls}
            value={form.gender}
            onChange={(e) => set("gender", e.target.value)}
          >
            <option value="">—</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="neutral">Neutral</option>
          </select>
        </PodcastField>
        <PodcastField label="Default style / mood">
          <select
            className={podcastInputCls}
            value={form.default_style || ""}
            onChange={(e) => set("default_style", e.target.value || null)}
          >
            {(ttsStyles || PODCAST_STYLES_FALLBACK).map((s) => (
              <option key={s.value} value={s.value} title={s.description}>
                {s.label}
              </option>
            ))}
          </select>
        </PodcastField>
        <div className="border border-slate-200 rounded-lg p-3 space-y-3">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.is_cloned}
              onChange={(e) => set("is_cloned", e.target.checked)}
              className="rounded border-slate-300 text-rose-600 focus:ring-rose-500"
            />
            This is a cloned voice
          </label>
          {form.is_cloned && (
            <>
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg p-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                Cloned voices require documented written consent from the voice
                owner. Record the consent document reference below.
              </div>
              <PodcastField label="Consent document reference" required>
                <input
                  className={podcastInputCls}
                  value={form.clone_consent_doc}
                  onChange={(e) => set("clone_consent_doc", e.target.value)}
                  placeholder="Link or file reference to signed consent"
                />
              </PodcastField>
              <div className="grid grid-cols-2 gap-3">
                <PodcastField label="Consent signed by" required>
                  <input
                    className={podcastInputCls}
                    value={form.clone_consent_signer}
                    onChange={(e) =>
                      set("clone_consent_signer", e.target.value)
                    }
                    placeholder="Voice owner's full name"
                  />
                </PodcastField>
                <PodcastField label="Consent date" required>
                  <input
                    type="date"
                    className={podcastInputCls}
                    value={form.clone_consent_date}
                    onChange={(e) => set("clone_consent_date", e.target.value)}
                  />
                </PodcastField>
              </div>
              {initial?.id && (
                <div className="flex items-center gap-3">
                  {initial?.clone_consent_file_path && (
                    <a
                      href={`${
                        AUTH_URL.startsWith("http")
                          ? AUTH_URL
                          : window.location.origin + AUTH_URL
                      }/podcast/voices/${initial.id}/consent-doc`}
                      onClick={async (e) => {
                        e.preventDefault();
                        try {
                          const url = await fetchPodcastAudioUrl(
                            `/podcast/voices/${initial.id}/consent-doc`,
                          );
                          window.open(url, "_blank");
                        } catch {
                          showToast("Could not open consent document.");
                        }
                      }}
                      className="text-xs text-slate-500 underline"
                    >
                      View stored consent document
                    </a>
                  )}
                  <label className="text-xs text-slate-500 underline cursor-pointer">
                    {initial?.clone_consent_file_path
                      ? "Replace document"
                      : "Upload signed document (pdf/jpg/png)"}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const fd = new FormData();
                        fd.append("file", file);
                        const resp = await authUpload(
                          `/podcast/voices/${initial.id}/consent-doc`,
                          fd,
                        );
                        if (resp.error) {
                          showToast("Failed to upload consent document.");
                        } else {
                          showToast("Consent document stored.", "success");
                          onSaved();
                        }
                      }}
                    />
                  </label>
                </div>
              )}
              <PodcastField label="Consent notes">
                <textarea
                  className={podcastInputCls}
                  rows={2}
                  value={form.clone_consent_notes}
                  onChange={(e) => set("clone_consent_notes", e.target.value)}
                />
              </PodcastField>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={previewVoice}
            disabled={previewing || !form.provider_voice_id.trim()}
            className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200 disabled:opacity-50"
          >
            {previewing ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Synthesizing…
              </>
            ) : (
              <>
                <Play className="w-3.5 h-3.5" /> Preview voice
              </>
            )}
          </button>
          {previewUrl && (
            <audio controls autoPlay src={previewUrl} className="h-8" />
          )}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Voice
          </button>
        </div>
      </div>
    </PodcastModal>
  );
};

// Fetch-on-click player for one cached line MP3 (review panel).
const PodcastLineAudio = ({ episodeId, index }) => {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  const play = async () => {
    setBusy(true);
    try {
      setUrl(
        await fetchPodcastAudioUrl(
          `/podcast/episodes/${episodeId}/lines/${index}/audio`,
        ),
      );
    } catch {
      showToast("This line's audio isn't rendered yet.");
    } finally {
      setBusy(false);
    }
  };

  if (url) return <audio controls autoPlay src={url} className="h-7" />;
  return (
    <button
      onClick={play}
      disabled={busy}
      className="text-xs text-slate-400 hover:text-slate-600 underline disabled:opacity-50"
    >
      {busy ? "Loading…" : "Play line"}
    </button>
  );
};

// Waveform player: server-computed peaks on a canvas with chapter markers,
// click-to-seek, and a live playhead. Endpoint-agnostic — episodes and
// intro/outro segment previews both use it.
const PodcastWaveform = ({ waveformEndpoint, audioEndpoint }) => {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const [data, setData] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [wf, url] = await Promise.all([
          authFetch(waveformEndpoint),
          fetchPodcastAudioUrl(audioEndpoint),
        ]);
        if (cancelled) return;
        if (wf.error) {
          setError(podcastErr(wf, "Could not load waveform."));
          return;
        }
        setData(wf);
        setAudioUrl(url);
      } catch {
        if (!cancelled) setError("Could not load waveform.");
      }
    })();
    return () => {
      cancelled = true;
      audioUrl && URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line
  }, [waveformEndpoint, audioEndpoint]);

  const durationMs = (data?.duration_seconds || 0) * 1000;

  const chapters = (() => {
    if (!data?.line_timings?.length) return [];
    const seen = new Set();
    const marks = [];
    data.line_timings.forEach((t) => {
      const seg = t.segment || 0;
      if (!seen.has(seg)) {
        seen.add(seg);
        marks.push({
          seg,
          start_ms: t.start_ms,
          title: data.topic_outline?.[seg] || `Part ${seg + 1}`,
        });
      }
    });
    return marks.length > 1 ? marks : [];
  })();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data?.peaks?.length) return;
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    ctx.clearRect(0, 0, width, height);
    const n = data.peaks.length;
    const barW = width / n;
    const playedBars = Math.floor(progress * n);
    data.peaks.forEach((p, i) => {
      const h = Math.max(2, p * height * 0.9);
      ctx.fillStyle = i <= playedBars && progress > 0 ? "#e11d48" : "#cbd5e1";
      ctx.fillRect(i * barW, (height - h) / 2, Math.max(1, barW - 0.5), h);
    });
    if (durationMs) {
      ctx.fillStyle = "#64748b";
      chapters.forEach((c) => {
        const x = (c.start_ms / durationMs) * width;
        ctx.fillRect(x, 0, 1.5, height);
      });
    }
  }, [data, progress, chapters, durationMs]);

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.target.getBoundingClientRect();
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * audio.duration;
  };

  if (error) return <p className="text-xs text-slate-400">{error}</p>;
  if (!data || !audioUrl)
    return <p className="text-xs text-slate-400">Analyzing audio…</p>;
  return (
    <div className="w-full space-y-1">
      <canvas
        ref={canvasRef}
        width={800}
        height={72}
        onClick={seek}
        className="w-full h-16 cursor-pointer rounded bg-slate-50"
        title="Click to seek"
      />
      {chapters.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          {chapters.map((c) => (
            <button
              key={c.seg}
              onClick={() => {
                const audio = audioRef.current;
                if (audio) audio.currentTime = c.start_ms / 1000;
              }}
              className="text-xs text-slate-500 hover:text-rose-600"
            >
              ▸ {c.title}
            </button>
          ))}
        </div>
      )}
      <audio
        ref={audioRef}
        controls
        src={audioUrl}
        className="w-full h-8"
        onTimeUpdate={(e) =>
          e.target.duration &&
          setProgress(e.target.currentTime / e.target.duration)
        }
      />
    </div>
  );
};

// Episode audio with an on-demand waveform view (peaks + chapter markers).
const PodcastEpisodeAudio = ({ episodeId }) => {
  const [showWave, setShowWave] = useState(false);
  if (showWave)
    return (
      <div className="w-full">
        <PodcastWaveform
          waveformEndpoint={`/podcast/episodes/${episodeId}/waveform`}
          audioEndpoint={`/podcast/episodes/${episodeId}/audio`}
        />
      </div>
    );
  return (
    <>
      <PodcastAudioPlayer endpoint={`/podcast/episodes/${episodeId}/audio`} />
      <button
        onClick={() => setShowWave(true)}
        className="text-xs text-slate-400 hover:text-slate-600 underline"
      >
        Waveform
      </button>
    </>
  );
};

// Versioned intro/outro segment editor (one per kind). Draft → Generate &
// Preview (waveform) → Approve. Approved versions are immutable; editing
// starts the next version. Episodes stamp the version they were rendered
// with, so approving v2 never changes already-rendered audio.
const SEGMENT_BUSY = ["queued", "generating"];

const PodcastSegmentEditor = ({
  showId,
  kind,
  voices,
  segments,
  onChanged,
}) => {
  const mine = segments.filter((s) => s.kind === kind); // newest first
  const active = mine.find((s) => s.status === "approved");
  const working = mine.find((s) => s.status !== "approved");

  const source = working || active;
  const [script, setScript] = useState(source?.script || "");
  const [voiceId, setVoiceId] = useState(source?.voice_id || "");
  const [volumeDb, setVolumeDb] = useState(source?.music_volume_db ?? -12);
  const [bedFile, setBedFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const rendering = working && SEGMENT_BUSY.includes(working.status);
  const previewReady = working?.status === "preview_ready";
  const dirty =
    script !== (source?.script || "") ||
    String(voiceId || "") !== String(source?.voice_id || "") ||
    Number(volumeDb) !== Number(source?.music_volume_db ?? -12);

  const label = kind === "intro" ? "Intro" : "Outro";

  // Save (or version-bump) the draft; returns the segment row or null.
  const saveDraft = async () => {
    const data = await authFetch(`/podcast/shows/${showId}/segments/${kind}`, {
      method: "PUT",
      body: JSON.stringify({
        script,
        voice_id: voiceId ? Number(voiceId) : null,
        music_volume_db: Number(volumeDb),
      }),
    });
    const err = podcastErr(data, `Failed to save ${kind} draft.`);
    if (err) {
      showToast(err);
      return null;
    }
    return data.segment;
  };

  const generate = async () => {
    if (!script.trim()) {
      showToast(`Write the ${kind} script first.`);
      return;
    }
    if (!voiceId) {
      showToast(`Choose a ${kind} voice first.`);
      return;
    }
    setBusy(true);
    const seg = await saveDraft();
    if (seg) {
      const res = await authFetch(`/podcast/segments/${seg.id}/generate`, {
        method: "POST",
        body: "{}",
      });
      const err = podcastErr(res, "Failed to start the preview render.");
      if (err) showToast(err);
      else showToast(`${label} preview render started.`, "success");
      onChanged();
    }
    setBusy(false);
  };

  const approve = async () => {
    setBusy(true);
    const res = await authFetch(`/podcast/segments/${working.id}/approve`, {
      method: "POST",
      body: "{}",
    });
    setBusy(false);
    const err = podcastErr(res, "Failed to approve the segment.");
    if (err) {
      showToast(err);
      return;
    }
    showToast(
      `${label} v${working.version} approved — new episodes will use it.`,
      "success",
    );
    onChanged();
  };

  const uploadBed = async () => {
    if (!bedFile) return;
    setBusy(true);
    // The bed attaches to a segment row, so make sure the draft exists first.
    const seg = working && !dirty ? working : await saveDraft();
    if (seg) {
      const fd = new FormData();
      fd.append("file", bedFile);
      const res = await authUpload(`/podcast/segments/${seg.id}/music-bed`, fd);
      const err = podcastErr(res, "Failed to upload music bed.");
      if (err) showToast(err);
      else {
        setBedFile(null);
        showToast("Music bed uploaded.", "success");
      }
      onChanged();
    }
    setBusy(false);
  };

  const removeBed = async () => {
    setBusy(true);
    const res = await authFetch(`/podcast/segments/${working.id}/music-bed`, {
      method: "DELETE",
    });
    setBusy(false);
    const err = podcastErr(res, "Failed to remove music bed.");
    if (err) showToast(err);
    else showToast("Music bed removed.", "success");
    onChanged();
  };

  const hasBed = Boolean(working?.music_bed_path);

  return (
    <div className="space-y-3 border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
          {label}
        </p>
        <div className="flex items-center gap-1.5">
          {active && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              v{active.version} live
            </span>
          )}
          {working && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
              v{working.version}{" "}
              {working.status === "failed" ? "failed" : "draft"}
            </span>
          )}
        </div>
      </div>
      <PodcastField label={`${label} script`}>
        <textarea
          className={podcastInputCls}
          rows={3}
          value={script}
          onChange={(e) => setScript(e.target.value)}
          disabled={rendering}
        />
      </PodcastField>
      <PodcastField label={`${label} voice`}>
        <select
          className={podcastInputCls}
          value={voiceId || ""}
          onChange={(e) => setVoiceId(e.target.value)}
          disabled={rendering}
        >
          <option value="">— None —</option>
          {voices.map((v) => (
            <option key={v.id} value={v.id}>
              {v.display_name} ({v.provider})
            </option>
          ))}
        </select>
      </PodcastField>
      <div className="space-y-1">
        <p className="text-xs font-medium text-slate-600">
          Music bed{" "}
          <span className="font-normal text-slate-400">
            — optional, ducked under the voice
          </span>
        </p>
        {hasBed ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-lg">
              Bed set
            </span>
            <label className="flex items-center gap-1.5 text-xs text-slate-600">
              Volume
              <input
                type="range"
                min={-40}
                max={0}
                step={1}
                value={volumeDb}
                onChange={(e) => setVolumeDb(Number(e.target.value))}
                disabled={rendering}
              />
              {volumeDb} dB
            </label>
            <button
              type="button"
              onClick={removeBed}
              disabled={busy || rendering}
              className="text-xs text-red-500 hover:underline disabled:opacity-50"
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept="audio/mpeg,.mp3"
              onChange={(e) => setBedFile(e.target.files?.[0] || null)}
              className="text-xs"
              disabled={rendering}
            />
            {bedFile && (
              <button
                type="button"
                onClick={uploadBed}
                disabled={busy}
                className="px-2 py-1 bg-slate-700 text-white text-xs rounded-lg disabled:opacity-50"
              >
                Upload
              </button>
            )}
          </div>
        )}
      </div>
      {working?.status === "failed" && working.error_message && (
        <p className="text-xs text-red-600">{working.error_message}</p>
      )}
      {previewReady && !dirty && (
        <div className="space-y-2">
          <PodcastWaveform
            waveformEndpoint={`/podcast/segments/${working.id}/waveform`}
            audioEndpoint={`/podcast/segments/${working.id}/audio`}
          />
          <button
            type="button"
            onClick={approve}
            disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            Approve v{working.version}
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={generate}
        disabled={busy || rendering}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 text-white text-xs font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50"
      >
        {rendering ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Sparkles className="w-3.5 h-3.5" />
        )}
        {rendering ? "Rendering preview…" : "Generate & Preview"}
      </button>
      {active && !working && (
        <p className="text-xs text-slate-400">
          Editing and generating a preview starts v{active.version + 1}; v
          {active.version} stays live until you approve the new version.
        </p>
      )}
    </div>
  );
};

// Fetches segment versions for a show and renders both editors; polls while a
// preview render is in flight.
const PodcastSegmentsPanel = ({ showId, voices }) => {
  const [segments, setSegments] = useState(null);

  const refresh = async () => {
    const data = await authFetch(`/podcast/shows/${showId}/segments`);
    if (!data.error) setSegments(data.segments || []);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [showId]);

  useEffect(() => {
    if (!segments?.some((s) => SEGMENT_BUSY.includes(s.status)))
      return undefined;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [segments]);

  if (!segments)
    return <p className="text-xs text-slate-400">Loading intro/outro…</p>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {["intro", "outro"].map((kind) => (
          <PodcastSegmentEditor
            key={`${kind}-${segments
              .filter((s) => s.kind === kind)
              .map((s) => `${s.id}:${s.status}`)
              .join(",")}`}
            showId={showId}
            kind={kind}
            voices={voices}
            segments={segments}
            onChanged={refresh}
          />
        ))}
      </div>
      {/* Copy pending human review (Hard Rule 8) */}
      <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
        Episodes keep the intro and outro version they were rendered with.
        Approving a new version affects new renders only.
      </p>
    </div>
  );
};

// Full-episode mix lanes (episode-production-card mockup): what the
// compositing pass actually applied to this render — music bed (intro sting →
// low ducked loop → outro sting) and room tone spanning the whole episode,
// sized from the manifest's resolved durations, with the reproducibility
// record behind a details toggle. Episodes without a composite pass (no
// audio.yaml for the show) render nothing.
const PodcastMixLanes = ({ manifest }) => {
  const [open, setOpen] = useState(false);
  let mix = manifest;
  if (typeof mix === "string") {
    try {
      mix = JSON.parse(mix);
    } catch {
      return null;
    }
  }
  if (!mix || (!mix.musicBed && !mix.roomTone)) return null;

  const bed = mix.musicBed;
  const tone = mix.roomTone;
  const duration = mix.voice?.durationSec || 0;
  // Sting widths as % of the episode, clamped so they stay visible on long
  // episodes and never swallow the loop on short ones.
  const pct = (sec) =>
    duration > 0 ? Math.min(35, Math.max(3, (sec / duration) * 100)) : 8;
  const introPct = bed?.resolved?.introSec > 0 ? pct(bed.resolved.introSec) : 0;
  const outroPct = bed?.resolved?.outroSec > 0 ? pct(bed.resolved.outroSec) : 0;
  const baseName = (p) =>
    String(p || "")
      .split(/[\\/]/)
      .pop();
  const loopStripes = {
    backgroundImage:
      "repeating-linear-gradient(90deg,#eef0f3 0 8px,#e8eaed 8px 9px)",
  };
  const toneStripes = {
    backgroundImage:
      "repeating-linear-gradient(90deg,#f0f1f3 0 3px,#e9ebee 3px 6px)",
  };
  const stingCls =
    "absolute inset-y-0 flex items-center justify-center text-[8px] font-semibold text-slate-500 bg-slate-200";

  return (
    <div className="mt-3 border-t border-slate-100 pt-2.5">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          Full-episode mix
        </span>
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-[10px] text-slate-400 hover:text-slate-600 underline"
        >
          {open ? "Hide details" : "Details"}
        </button>
      </div>
      <div className="space-y-1">
        {bed && (
          <div className="flex items-center gap-2">
            <span className="flex-none w-20 text-[11px] font-semibold text-slate-500">
              Music bed
            </span>
            <div className="flex-1 h-5 rounded border border-slate-200 relative overflow-hidden bg-white">
              {introPct > 0 && (
                <div
                  className={`${stingCls} left-0`}
                  style={{ width: `${introPct}%` }}
                >
                  INTRO
                </div>
              )}
              <div
                className="absolute inset-y-0 flex items-center justify-center text-[8px] text-slate-400 whitespace-nowrap overflow-hidden"
                style={{
                  left: `${introPct}%`,
                  right: `${outroPct}%`,
                  ...loopStripes,
                }}
              >
                {bed.duck?.enabled
                  ? "low loop · ducked under speech"
                  : "low loop"}
              </div>
              {outroPct > 0 && (
                <div
                  className={`${stingCls} right-0`}
                  style={{ width: `${outroPct}%` }}
                >
                  OUTRO
                </div>
              )}
            </div>
            <span className="flex-none w-14 text-right text-[10px] text-slate-400 tabular-nums">
              {bed.bodyGainDb} dB
            </span>
          </div>
        )}
        {tone && (
          <div className="flex items-center gap-2">
            <span className="flex-none w-20 text-[11px] font-semibold text-slate-500">
              Room tone
            </span>
            <div
              className="flex-1 h-5 rounded border border-slate-200"
              style={toneStripes}
            />
            <span className="flex-none w-14 text-right text-[10px] text-slate-400 tabular-nums">
              {tone.gainDb} dB
            </span>
          </div>
        )}
      </div>
      {open && (
        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-100 p-2.5 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-500">
          {bed?.duck?.enabled && (
            <>
              <span>
                Duck{" "}
                <b className="text-slate-700 font-medium">
                  {bed.duck.thresholdDb} dB · {bed.duck.ratio}:1
                </b>
              </span>
              <span>
                Attack / release{" "}
                <b className="text-slate-700 font-medium">
                  {bed.duck.attackMs} / {bed.duck.releaseMs} ms
                </b>
              </span>
            </>
          )}
          {bed && (
            <>
              <span>
                Crossfade{" "}
                <b className="text-slate-700 font-medium">
                  {bed.crossfadeSec}s
                </b>
              </span>
              <span>
                Bed body{" "}
                <b className="text-slate-700 font-medium">
                  {Math.round(bed.resolved?.bodySec || 0)}s
                </b>
              </span>
              <span
                className="col-span-2 truncate"
                title={[bed.introFile, bed.loopFile, bed.outroFile]
                  .filter(Boolean)
                  .join("\n")}
              >
                Bed files:{" "}
                {[bed.introFile, bed.loopFile, bed.outroFile]
                  .filter(Boolean)
                  .map(baseName)
                  .join(" → ")}
              </span>
            </>
          )}
          {tone && (
            <span className="col-span-2 truncate" title={tone.file}>
              Room tone: {baseName(tone.file)}
            </span>
          )}
          <span
            className="col-span-2 text-slate-400 truncate"
            title={mix.ffmpegVersion}
          >
            {String(mix.ffmpegVersion || "").split(" Copyright")[0]}
          </span>
        </div>
      )}
    </div>
  );
};

// Script viewer with inline editing (approval gate / ready / failed) and
// per-line re-voice. Edited lines re-synthesize individually on the next
// audio run thanks to per-line fingerprints.
const PodcastScriptModal = ({ episode, data, isAdmin, onClose, onChanged }) => {
  const initialLines = data.script_json?.lines || [];
  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState(initialLines);
  const [saving, setSaving] = useState(false);
  const [revoicing, setRevoicing] = useState(null);
  const editableStatus = ["script_ready", "ready", "failed"].includes(
    episode.status,
  );
  const canRevoice = ["ready", "failed"].includes(episode.status);
  const roster = [...new Set(initialLines.map((l) => l.speaker))];

  const setLine = (i, patch) =>
    setLines((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const saveScript = async () => {
    if (lines.some((l) => !(l.text || "").trim())) {
      showToast("Every line needs text — delete empty lines first.");
      return;
    }
    setSaving(true);
    const resp = await authFetch(`/podcast/episodes/${episode.id}/script`, {
      method: "PUT",
      body: JSON.stringify({ lines }),
    });
    setSaving(false);
    const err = podcastErr(resp, "Failed to save script.");
    if (err) {
      showToast(err);
      return;
    }
    showToast(
      "Script saved. Text and gain changes apply on the next audio run.",
      "success",
    );
    setEditing(false);
    onChanged();
  };

  const removeLine = (i) => setLines((prev) => prev.filter((_, j) => j !== i));

  const revoiceLine = async (i) => {
    setRevoicing(i);
    const resp = await authFetch(
      `/podcast/episodes/${episode.id}/regenerate-line`,
      { method: "POST", body: JSON.stringify({ line_index: i }) },
    );
    setRevoicing(null);
    const err = podcastErr(resp, "Failed to queue re-voice.");
    if (err) {
      showToast(err);
      return;
    }
    showToast("Line queued for re-voice — audio is rebuilding.", "success");
    onChanged();
    onClose();
  };

  return (
    <PodcastModal
      title={`Script — ${episode.title || episode.topic}`}
      onClose={onClose}
      wide
    >
      <div className="space-y-3">
        {isAdmin && editableStatus && initialLines.length > 0 && (
          <div className="flex justify-end gap-2">
            {editing ? (
              <>
                <button
                  onClick={() => {
                    setLines(initialLines);
                    setEditing(false);
                  }}
                  className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={saveScript}
                  disabled={saving}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                >
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Save script
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                <Edit3 className="w-3.5 h-3.5" /> Edit script
              </button>
            )}
          </div>
        )}
        {(editing ? lines : initialLines).map((line, i) => (
          <div key={i}>
            {editing ? (
              <div className="border border-slate-200 rounded-lg p-2 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <select
                    value={line.speaker}
                    onChange={(e) => setLine(i, { speaker: e.target.value })}
                    className="text-xs font-semibold text-rose-600 border border-slate-200 rounded px-1.5 py-1"
                  >
                    {roster.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-xs text-slate-500">
                      Gain
                      <input
                        type="number"
                        min="-12"
                        max="12"
                        step="0.5"
                        value={line.gain_db ?? 0}
                        onChange={(e) =>
                          setLine(i, { gain_db: Number(e.target.value) })
                        }
                        className="w-16 text-xs border border-slate-200 rounded px-1.5 py-1"
                        aria-label={`Line ${i + 1} gain (dB)`}
                      />
                      dB
                    </label>
                    <button
                      onClick={() => removeLine(i)}
                      className="text-xs text-red-400 hover:text-red-600"
                      aria-label={`Delete line ${i + 1}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <textarea
                  value={line.text}
                  onChange={(e) => setLine(i, { text: e.target.value })}
                  rows={Math.min(
                    6,
                    Math.ceil((line.text || "").length / 90) + 1,
                  )}
                  className="w-full text-sm text-slate-700 border border-slate-200 rounded-lg px-2 py-1.5"
                />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-rose-600">
                    {line.speaker}
                    {line.gain_db ? (
                      <span className="ml-2 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-normal">
                        {line.gain_db > 0 ? "+" : ""}
                        {line.gain_db} dB
                      </span>
                    ) : null}
                  </p>
                  {isAdmin && canRevoice && (
                    <span className="flex items-center gap-3">
                      <PodcastLineAudio episodeId={episode.id} index={i} />
                      <button
                        onClick={() => revoiceLine(i)}
                        disabled={revoicing !== null}
                        className="text-xs text-slate-400 hover:text-slate-600 underline disabled:opacity-50"
                      >
                        {revoicing === i ? "Queuing…" : "Re-voice line"}
                      </button>
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-700">{line.text}</p>
              </>
            )}
          </div>
        ))}
        {!initialLines.length && data.script_text && (
          <pre className="text-sm text-slate-700 whitespace-pre-wrap">
            {data.script_text}
          </pre>
        )}
        {Array.isArray(data.rag_sources) && data.rag_sources.length > 0 && (
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Knowledge base sources
            </p>
            <ul className="text-xs text-slate-500 list-disc pl-4 space-y-0.5">
              {data.rag_sources.map((src, i) => (
                <li key={i}>{src.title || src.source}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </PodcastModal>
  );
};

// Low-bitrate preview of the first script lines, rendered on demand while
// the episode sits at the approval gate.
const PodcastPreviewButton = ({ episodeId }) => {
  const [state, setState] = useState("idle"); // idle | rendering | ready
  const [url, setUrl] = useState(null);

  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  const start = async () => {
    setState("rendering");
    const data = await authFetch(`/podcast/episodes/${episodeId}/preview`, {
      method: "POST",
      body: "{}",
    });
    const err = podcastErr(data, "Preview failed to start.");
    if (err) {
      showToast(err);
      setState("idle");
      return;
    }
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const audioUrl = await fetchPodcastAudioUrl(
          `/podcast/episodes/${episodeId}/preview-audio`,
        );
        clearInterval(poll);
        setUrl(audioUrl);
        setState("ready");
      } catch {
        if (attempts > 30) {
          clearInterval(poll);
          setState("idle");
          showToast("Preview timed out. Please try again.");
        }
      }
    }, 3000);
  };

  if (state === "ready" && url)
    return <audio controls src={url} className="h-8" />;
  return (
    <button
      onClick={start}
      disabled={state === "rendering"}
      className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200 disabled:opacity-50"
    >
      {state === "rendering" ? (
        <>
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Rendering…
        </>
      ) : (
        <>
          <Play className="w-3.5 h-3.5" /> Preview
        </>
      )}
    </button>
  );
};

// Start/end trim for an uploaded show asset; first trim keeps a server-side
// .orig backup so "Restore original" can undo.
const PodcastAssetTrim = ({ showId, asset }) => {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState("0");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (action) => {
    setBusy(true);
    const body =
      action === "restore"
        ? { asset, action: "restore" }
        : {
            asset,
            start_seconds: Number(start) || 0,
            ...(end.trim() !== "" ? { end_seconds: Number(end) } : {}),
          };
    const data = await authFetch(`/podcast/shows/${showId}/trim-asset`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    setBusy(false);
    const err = podcastErr(data, "Trim failed.");
    if (err) {
      showToast(err);
      return;
    }
    showToast(
      action === "restore" ? "Original restored." : "Asset trimmed.",
      "success",
    );
    if (action !== "restore") setOpen(false);
  };

  if (!open)
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-slate-500 hover:text-slate-700 underline"
      >
        Trim
      </button>
    );
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <input
        type="number"
        min="0"
        step="0.1"
        value={start}
        onChange={(e) => setStart(e.target.value)}
        placeholder="start s"
        className="w-16 text-xs border border-slate-200 rounded px-1.5 py-1"
        aria-label="Trim start (seconds)"
      />
      <span className="text-xs text-slate-400">→</span>
      <input
        type="number"
        min="0"
        step="0.1"
        value={end}
        onChange={(e) => setEnd(e.target.value)}
        placeholder="end s"
        className="w-16 text-xs border border-slate-200 rounded px-1.5 py-1"
        aria-label="Trim end (seconds, blank = to end)"
      />
      <button
        type="button"
        onClick={() => run("trim")}
        disabled={busy}
        className="text-xs px-2 py-1 rounded bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50"
      >
        Apply
      </button>
      <button
        type="button"
        onClick={() => run("restore")}
        disabled={busy}
        className="text-xs text-slate-500 hover:underline"
      >
        Restore original
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="text-xs text-slate-400 hover:underline"
      >
        Cancel
      </button>
    </span>
  );
};

const PODCAST_WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Recurring-episode schedule for a show (one per show). Runs clone the
// template episode and stop at script_ready — the approval gate still applies.
const PodcastScheduleSection = ({ showId, episodes }) => {
  const [schedule, setSchedule] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    enabled: true,
    cadence: "weekly",
    day_of_week: 0,
    day_of_month: 1,
    hour_local: 6,
    template_episode_id: "",
    topic_template: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const showEpisodes = episodes.filter((e) => e.show_id === showId);

  useEffect(() => {
    authFetch(`/podcast/shows/${showId}/schedule`).then((data) => {
      setLoaded(true);
      if (data.error || !data.schedule) return;
      const s = data.schedule;
      setSchedule(s);
      setForm({
        enabled: s.enabled,
        cadence: s.cadence,
        day_of_week: s.day_of_week ?? 0,
        day_of_month: s.day_of_month ?? 1,
        hour_local: s.hour_local ?? 6,
        template_episode_id: s.template_episode_id || "",
        topic_template: s.topic_template || "",
      });
    });
    // eslint-disable-next-line
  }, [showId]);

  const save = async () => {
    if (!form.template_episode_id) {
      showToast("Pick a template episode for the schedule.");
      return;
    }
    setSaving(true);
    const data = await authFetch(`/podcast/shows/${showId}/schedule`, {
      method: "PUT",
      body: JSON.stringify({
        ...form,
        template_episode_id: Number(form.template_episode_id),
        day_of_week:
          form.cadence === "weekly" ? Number(form.day_of_week) : null,
        day_of_month:
          form.cadence === "monthly" ? Number(form.day_of_month) : null,
        hour_local: Number(form.hour_local),
      }),
    });
    setSaving(false);
    const err = podcastErr(data, "Failed to save schedule.");
    if (err) {
      showToast(err);
      return;
    }
    setSchedule(data.schedule);
    showToast("Schedule saved.", "success");
  };

  const remove = async () => {
    if (!window.confirm("Remove this show's episode schedule?")) return;
    const data = await authFetch(`/podcast/shows/${showId}/schedule`, {
      method: "DELETE",
    });
    if (data.error) {
      showToast("Failed to remove schedule.");
      return;
    }
    setSchedule(null);
    showToast("Schedule removed.", "success");
  };

  if (!loaded) return null;
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-3">
      <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
        Recurring episodes
      </p>
      <p className="text-xs text-slate-500">
        Automatically drafts a new episode on a cadence, copying the template
        episode's speakers and prompt. Scripts still wait for your approval
        before audio is generated. Times are US Arizona (Phoenix).
      </p>
      <div className="grid grid-cols-2 gap-3">
        <PodcastField label="Template episode" required>
          <select
            className={podcastInputCls}
            value={form.template_episode_id}
            onChange={(e) => set("template_episode_id", e.target.value)}
          >
            <option value="">— Pick an episode —</option>
            {showEpisodes.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title || e.topic || `Episode ${e.id}`}
              </option>
            ))}
          </select>
        </PodcastField>
        <PodcastField label="Topic (optional, {date} supported)">
          <input
            className={podcastInputCls}
            value={form.topic_template}
            onChange={(e) => set("topic_template", e.target.value)}
            placeholder="Weekly update for {date}"
          />
        </PodcastField>
        <PodcastField label="Cadence">
          <select
            className={podcastInputCls}
            value={form.cadence}
            onChange={(e) => set("cadence", e.target.value)}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </PodcastField>
        {form.cadence === "weekly" && (
          <PodcastField label="Day of week">
            <select
              className={podcastInputCls}
              value={form.day_of_week}
              onChange={(e) => set("day_of_week", e.target.value)}
            >
              {PODCAST_WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </PodcastField>
        )}
        {form.cadence === "monthly" && (
          <PodcastField label="Day of month (1–28)">
            <input
              type="number"
              min="1"
              max="28"
              className={podcastInputCls}
              value={form.day_of_month}
              onChange={(e) => set("day_of_month", e.target.value)}
            />
          </PodcastField>
        )}
        <PodcastField label="Hour (0–23)">
          <input
            type="number"
            min="0"
            max="23"
            className={podcastInputCls}
            value={form.hour_local}
            onChange={(e) => set("hour_local", e.target.value)}
          />
        </PodcastField>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => set("enabled", e.target.checked)}
          className="rounded"
        />
        Enabled
      </label>
      {schedule?.next_run_at && (
        <p className="text-xs text-slate-400">
          Next run: {new Date(schedule.next_run_at).toLocaleString()}
          {schedule.last_run_at &&
            ` · Last run: ${new Date(schedule.last_run_at).toLocaleString()}`}
        </p>
      )}
      {schedule?.last_error && (
        <p className="text-xs text-red-600">
          Last run failed: {schedule.last_error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-50"
        >
          {saving
            ? "Saving…"
            : schedule
              ? "Update schedule"
              : "Create schedule"}
        </button>
        {schedule && (
          <button
            type="button"
            onClick={remove}
            className="text-xs text-red-500 hover:underline"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

const PodcastShowForm = ({
  facilityId,
  voices,
  episodes = [],
  initial,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    prompt_template: initial?.prompt_template || "",
    script_model: initial?.script_model || "gpt-4o-mini",
    tts_provider: initial?.tts_provider || "azure",
    azure_speech_key: "",
    azure_speech_region: initial?.azure_speech_region || "",
    background_volume_db: initial?.background_volume_db ?? -12,
    ad_midroll_position_pct: initial?.ad_midroll_position_pct ?? 50,
    line_gap_ms: initial?.line_gap_ms ?? 300,
    speaker_gap_ms: initial?.speaker_gap_ms ?? 500,
    midroll_times_text: Array.isArray(initial?.ad_midroll_times)
      ? initial.ad_midroll_times.join(", ")
      : "",
    author: initial?.author || "",
    category: initial?.category || "",
    explicit: initial?.explicit || false,
  });
  // The wizard persists a draft show as soon as Basics is complete, so later
  // steps (uploads, intro/outro) always have a show id to attach assets to.
  const [showId, setShowId] = useState(initial?.id || null);
  const [showMeta, setShowMeta] = useState(initial || null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkUploading, setArtworkUploading] = useState(false);
  const [hasArtwork, setHasArtwork] = useState(initial?.has_artwork || false);

  const uploadArtwork = async () => {
    if (!artworkFile || !showId) return;
    setArtworkUploading(true);
    const fd = new FormData();
    fd.append("file", artworkFile);
    const data = await authUpload(`/podcast/shows/${showId}/artwork`, fd);
    setArtworkUploading(false);
    if (data.error) {
      showToast("Failed to upload artwork.");
    } else {
      setHasArtwork(true);
      setArtworkFile(null);
      showToast("Artwork uploaded.", "success");
    }
  };

  const feedUrl = showMeta?.feed_token
    ? `${
        AUTH_URL.startsWith("http")
          ? AUTH_URL
          : window.location.origin + AUTH_URL
      }/podcast/feed/${showMeta.feed_token}.xml`
    : null;

  const copyFeedUrl = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      showToast("Feed URL copied.", "success");
    } catch {
      showToast("Could not copy — select the URL manually.");
    }
  };
  const [saving, setSaving] = useState(false);
  const [clearAzureKey, setClearAzureKey] = useState(false);
  const [bgFile, setBgFile] = useState(null);
  const [bgUploading, setBgUploading] = useState(false);
  const [hasBackground, setHasBackground] = useState(
    initial?.has_background_audio || false,
  );
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const uploadBackground = async () => {
    if (!bgFile || !showId) return;
    setBgUploading(true);
    const fd = new FormData();
    fd.append("file", bgFile);
    const data = await authUpload(
      `/podcast/shows/${showId}/background-audio`,
      fd,
    );
    setBgUploading(false);
    if (data.error) {
      showToast("Failed to upload background audio.");
    } else {
      setHasBackground(true);
      setBgFile(null);
      showToast("Background audio uploaded.", "success");
    }
  };

  const removeBackground = async () => {
    if (!showId) return;
    const data = await authFetch(`/podcast/shows/${showId}/background-audio`, {
      method: "DELETE",
    });
    if (data.error) {
      showToast("Failed to remove background audio.");
    } else {
      setHasBackground(false);
      showToast("Background audio removed.", "success");
    }
  };

  const [stingerFile, setStingerFile] = useState(null);
  const [stingerUploading, setStingerUploading] = useState(false);
  const [hasStinger, setHasStinger] = useState(initial?.has_stinger || false);

  const uploadStinger = async () => {
    if (!stingerFile || !showId) return;
    setStingerUploading(true);
    const fd = new FormData();
    fd.append("file", stingerFile);
    const data = await authUpload(`/podcast/shows/${showId}/stinger-audio`, fd);
    setStingerUploading(false);
    if (data.error) {
      showToast("Failed to upload stinger.");
    } else {
      setHasStinger(true);
      setStingerFile(null);
      showToast("Stinger uploaded.", "success");
    }
  };

  const removeStinger = async () => {
    if (!showId) return;
    const data = await authFetch(`/podcast/shows/${showId}/stinger-audio`, {
      method: "DELETE",
    });
    if (data.error) {
      showToast("Failed to remove stinger.");
    } else {
      setHasStinger(false);
      showToast("Stinger removed.", "success");
    }
  };

  const AD_SLOTS = [
    {
      key: "preroll",
      label: "Pre-roll",
      hint: "Plays after intro, before content",
    },
    { key: "midroll", label: "Mid-roll", hint: "Inserted mid-content" },
    {
      key: "postroll",
      label: "Post-roll",
      hint: "Plays after content, before outro",
    },
  ];
  const [adFiles, setAdFiles] = useState({
    preroll: null,
    midroll: null,
    postroll: null,
  });
  const [adUploading, setAdUploading] = useState({
    preroll: false,
    midroll: false,
    postroll: false,
  });
  const [hasAd, setHasAd] = useState({
    preroll: initial?.has_ad_preroll || false,
    midroll: initial?.has_ad_midroll || false,
    postroll: initial?.has_ad_postroll || false,
  });

  const uploadAd = async (slot) => {
    const file = adFiles[slot];
    if (!file || !showId) return;
    setAdUploading((p) => ({ ...p, [slot]: true }));
    const fd = new FormData();
    fd.append("file", file);
    const data = await authUpload(
      `/podcast/shows/${showId}/ad-audio/${slot}`,
      fd,
    );
    setAdUploading((p) => ({ ...p, [slot]: false }));
    if (data.error) {
      showToast(`Failed to upload ${slot} ad.`);
    } else {
      setHasAd((p) => ({ ...p, [slot]: true }));
      setAdFiles((p) => ({ ...p, [slot]: null }));
      showToast(`${slot} ad uploaded.`, "success");
    }
  };

  const removeAd = async (slot) => {
    if (!showId) return;
    const data = await authFetch(`/podcast/shows/${showId}/ad-audio/${slot}`, {
      method: "DELETE",
    });
    if (data.error) {
      showToast(`Failed to remove ${slot} ad.`);
    } else {
      setHasAd((p) => ({ ...p, [slot]: false }));
      showToast(`${slot} ad removed.`, "success");
    }
  };

  const buildPayload = () => {
    const payload = {
      ...form,
      facility_id: facilityId,
      azure_speech_region: form.azure_speech_region.trim() || null,
      ad_midroll_times: form.midroll_times_text
        .split(/[\s,]+/)
        .map((t) => Number(t))
        .filter((t) => !Number.isNaN(t) && t > 0),
    };
    delete payload.midroll_times_text;
    // Write-only secret: omit unless the user typed a new key or asked to clear it.
    if (clearAzureKey) {
      payload.azure_speech_key = "";
    } else if (!form.azure_speech_key.trim()) {
      delete payload.azure_speech_key;
    }
    return payload;
  };

  // Create the draft on first advance, then PUT the full form on every step
  // change so nothing is lost if the wizard is closed mid-way. The POST only
  // persists basics; the follow-up PUTs carry the rest (script model,
  // publishing metadata, ad config — fields the create endpoint ignores).
  const persistDraft = async () => {
    if (!form.title.trim()) return false;
    setDraftSaving(true);
    const body = JSON.stringify(buildPayload());
    const data = showId
      ? await authFetch(`/podcast/shows/${showId}`, { method: "PUT", body })
      : await authFetch(`/podcast/shows`, { method: "POST", body });
    setDraftSaving(false);
    const err = podcastErr(data, "Failed to save show.");
    if (err) {
      showToast(err);
      return false;
    }
    if (data.show) {
      setShowId(data.show.id);
      setShowMeta(data.show);
    }
    return true;
  };

  const finish = async () => {
    setSaving(true);
    const ok = await persistDraft();
    setSaving(false);
    if (!ok) return;
    showToast(initial?.id ? "Show updated." : "Show created.", "success");
    onSaved();
  };

  // Once a draft exists, closing must still refresh the show list.
  const close = () => (showId ? onSaved() : onClose());

  const steps = [
    {
      key: "basics",
      title: "Basics",
      nextDisabled: !form.title.trim(),
      content: (
        <div className="space-y-4">
          <PodcastField label="Title" required>
            <input
              className={podcastInputCls}
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
            />
          </PodcastField>
          <PodcastField label="Description">
            <textarea
              className={podcastInputCls}
              rows={2}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </PodcastField>
        </div>
      ),
    },
    {
      key: "script",
      title: "Script",
      content: (
        <div className="space-y-4">
          <PodcastField label="Script prompt template">
            <textarea
              className={podcastInputCls}
              rows={4}
              value={form.prompt_template}
              onChange={(e) => set("prompt_template", e.target.value)}
              placeholder="Optional. Instructions for the AI script writer — tone, format, audience. Leave blank for the default template."
            />
          </PodcastField>
          <PodcastField label="Script model">
            <select
              className={podcastInputCls}
              value={form.script_model}
              onChange={(e) => set("script_model", e.target.value)}
            >
              {[
                { value: "gpt-4o-mini", label: "GPT-4o Mini" },
                { value: "gpt-4o", label: "GPT-4o" },
                { value: "gpt-4.1", label: "GPT-4.1" },
                { value: "gpt-5-mini", label: "GPT-5 Mini" },
                { value: "gpt-5.1", label: "GPT-5.1" },
                { value: "gpt-5.2", label: "GPT-5.2" },
              ].map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </PodcastField>
        </div>
      ),
    },
    {
      key: "tts",
      title: "Voice & TTS",
      content: (
        <div className="space-y-4">
          <PodcastField label="Default TTS provider">
            <select
              className={podcastInputCls}
              value={form.tts_provider}
              onChange={(e) => set("tts_provider", e.target.value)}
            >
              {PODCAST_PROVIDERS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </PodcastField>
          <div className="space-y-3 border border-slate-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Azure Speech credentials (optional)
            </p>
            <p className="text-xs text-slate-500">
              Give this podcast its own Azure Speech key. Leave blank to use the
              platform default.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <PodcastField label="Azure Speech key">
                <input
                  type="password"
                  autoComplete="new-password"
                  className={podcastInputCls}
                  value={form.azure_speech_key}
                  onChange={(e) => set("azure_speech_key", e.target.value)}
                  disabled={clearAzureKey}
                  placeholder={
                    showMeta?.has_azure_key
                      ? "Configured — leave blank to keep"
                      : "Not set"
                  }
                />
              </PodcastField>
              <PodcastField label="Azure region">
                <input
                  className={podcastInputCls}
                  value={form.azure_speech_region}
                  onChange={(e) => set("azure_speech_region", e.target.value)}
                  placeholder="e.g. eastus"
                />
              </PodcastField>
            </div>
            {showMeta?.has_azure_key && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={clearAzureKey}
                  onChange={(e) => setClearAzureKey(e.target.checked)}
                />
                Remove the stored key (fall back to the platform default)
              </label>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "segments",
      title: "Intro & outro",
      content: showId ? (
        <PodcastSegmentsPanel showId={showId} voices={voices} />
      ) : (
        <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
          Preparing the show draft…
        </p>
      ),
    },
    {
      key: "ads",
      title: "Ad slots",
      content: (
        <div className="space-y-4">
          <div className="border border-slate-200 rounded-lg p-3 space-y-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Ad slots
            </p>
            <p className="text-xs text-slate-500">
              Upload pre-roll, mid-roll, and post-roll ad clips (mp3). Ad clips
              are baked into the episode audio at generation time.
            </p>
            {AD_SLOTS.map(({ key, label, hint }) => (
              <div key={key} className="space-y-1">
                <p className="text-xs font-medium text-slate-600">
                  {label}{" "}
                  <span className="font-normal text-slate-400">— {hint}</span>
                </p>
                {hasAd[key] ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-lg">
                      {label} set
                    </span>
                    {showId && (
                      <>
                        <PodcastAssetTrim showId={showId} asset={`ad_${key}`} />
                        <button
                          type="button"
                          onClick={() => removeAd(key)}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  showId && (
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={(e) =>
                          setAdFiles((p) => ({
                            ...p,
                            [key]: e.target.files?.[0] || null,
                          }))
                        }
                        className="text-xs text-slate-600 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => uploadAd(key)}
                        disabled={!adFiles[key] || adUploading[key]}
                        className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50"
                      >
                        {adUploading[key] ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Upload className="w-3.5 h-3.5" />
                        )}
                        Upload
                      </button>
                    </div>
                  )
                )}
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <PodcastField label="Mid-roll position (% of content)">
                <input
                  type="number"
                  step="5"
                  min="0"
                  max="100"
                  className={podcastInputCls}
                  value={form.ad_midroll_position_pct}
                  onChange={(e) =>
                    set("ad_midroll_position_pct", Number(e.target.value))
                  }
                />
                <p className="text-xs text-slate-400 mt-1">
                  Used when no mid-roll times are set.
                </p>
              </PodcastField>
              <PodcastField label="Mid-roll times (minutes)">
                <input
                  type="text"
                  className={podcastInputCls}
                  value={form.midroll_times_text}
                  onChange={(e) => set("midroll_times_text", e.target.value)}
                  placeholder="e.g. 10, 25"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Comma-separated. Each snaps to the nearest natural break in
                  the conversation. Overrides the % position.
                </p>
              </PodcastField>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "audio",
      title: "Audio & pacing",
      content: (
        <div className="space-y-4">
          <div className="border border-slate-200 rounded-lg p-3 space-y-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Background audio
            </p>
            <p className="text-xs text-slate-500">
              Optional music or ambient track looped under the episode.
            </p>
            {hasBackground ? (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-lg">
                  Background audio set
                </span>
                {showId && (
                  <>
                    <PodcastAssetTrim showId={showId} asset="background" />
                    <button
                      type="button"
                      onClick={removeBackground}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            ) : (
              showId && (
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={(e) => setBgFile(e.target.files?.[0] || null)}
                    className="text-xs text-slate-600 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                  />
                  <button
                    type="button"
                    onClick={uploadBackground}
                    disabled={!bgFile || bgUploading}
                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50"
                  >
                    {bgUploading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    Upload
                  </button>
                </div>
              )
            )}
            <PodcastField label="Background volume (dB)">
              <input
                type="number"
                step="1"
                min="-40"
                max="0"
                className={podcastInputCls}
                value={form.background_volume_db}
                onChange={(e) =>
                  set("background_volume_db", Number(e.target.value))
                }
              />
            </PodcastField>
          </div>
          <div className="border border-slate-200 rounded-lg p-3 space-y-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Pacing & transitions
            </p>
            <div className="grid grid-cols-2 gap-3">
              <PodcastField label="Line gap (ms)">
                <input
                  type="number"
                  step="50"
                  min="0"
                  max="3000"
                  className={podcastInputCls}
                  value={form.line_gap_ms}
                  onChange={(e) => set("line_gap_ms", Number(e.target.value))}
                />
                <p className="text-xs text-slate-400 mt-1">
                  Silence between lines of the same speaker.
                </p>
              </PodcastField>
              <PodcastField label="Speaker-change gap (ms)">
                <input
                  type="number"
                  step="50"
                  min="0"
                  max="5000"
                  className={podcastInputCls}
                  value={form.speaker_gap_ms}
                  onChange={(e) =>
                    set("speaker_gap_ms", Number(e.target.value))
                  }
                />
                <p className="text-xs text-slate-400 mt-1">
                  Slightly longer beat when the speaker changes.
                </p>
              </PodcastField>
            </div>
            <p className="text-xs text-slate-500">
              Segment stinger: a short transition sound played between outline
              segments of multi-topic episodes.
            </p>
            {hasStinger ? (
              <div className="flex items-center gap-3">
                <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-lg">
                  Stinger set
                </span>
                {showId && (
                  <>
                    <PodcastAudioPlayer
                      endpoint={`/podcast/shows/${showId}/stinger-audio`}
                      label="Play stinger"
                    />
                    <PodcastAssetTrim showId={showId} asset="stinger" />
                    <button
                      type="button"
                      onClick={removeStinger}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            ) : (
              showId && (
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={(e) =>
                      setStingerFile(e.target.files?.[0] || null)
                    }
                    className="text-xs text-slate-600 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                  />
                  <button
                    type="button"
                    onClick={uploadStinger}
                    disabled={!stingerFile || stingerUploading}
                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50"
                  >
                    {stingerUploading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    Upload
                  </button>
                </div>
              )
            )}
          </div>
        </div>
      ),
    },
    {
      key: "publish",
      title: "Publishing",
      content: (
        <div className="space-y-4">
          {showId && (
            <PodcastScheduleSection showId={showId} episodes={episodes} />
          )}
          <div className="border border-slate-200 rounded-lg p-3 space-y-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Publishing (RSS feed)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <PodcastField label="Author">
                <input
                  className={podcastInputCls}
                  value={form.author}
                  onChange={(e) => set("author", e.target.value)}
                  placeholder="Shown in podcast apps"
                />
              </PodcastField>
              <PodcastField label="Category">
                <input
                  className={podcastInputCls}
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  placeholder="e.g. Business"
                />
              </PodcastField>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={form.explicit}
                onChange={(e) => set("explicit", e.target.checked)}
                className="rounded"
              />
              Mark feed as explicit
            </label>
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1">
                Cover artwork (jpg/png, square, min 1400px recommended)
              </p>
              {hasArtwork ? (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-lg">
                    Artwork set
                  </span>
                  {showId && (
                    <label className="text-xs text-slate-500 underline cursor-pointer">
                      Replace
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => {
                          setArtworkFile(e.target.files?.[0] || null);
                          setHasArtwork(false);
                        }}
                      />
                    </label>
                  )}
                </div>
              ) : (
                showId && (
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={(e) =>
                        setArtworkFile(e.target.files?.[0] || null)
                      }
                      className="text-xs text-slate-600 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                    />
                    <button
                      type="button"
                      onClick={uploadArtwork}
                      disabled={!artworkFile || artworkUploading}
                      className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50"
                    >
                      {artworkUploading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Upload className="w-3.5 h-3.5" />
                      )}
                      Upload
                    </button>
                  </div>
                )
              )}
            </div>
            {feedUrl && (
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">
                  RSS feed URL — paste into any podcast app, or submit to
                  directories. Published episodes only. Treat it like a
                  password; anyone with the link can listen.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={feedUrl}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 text-xs font-mono border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-slate-50"
                  />
                  <button
                    type="button"
                    onClick={copyFeedUrl}
                    className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <PodcastModal
      title={initial?.id ? "Edit Show" : "New Show"}
      onClose={close}
      wide
    >
      <WizardShell
        accent="rose"
        steps={steps}
        onStepChange={persistDraft}
        onFinish={finish}
        finishLabel={initial?.id ? "Save & Close" : "Create Show"}
        busy={saving || draftSaving}
        onCancel={close}
        cancelLabel="Close"
      />
    </PodcastModal>
  );
};

const PodcastEpisodeForm = ({
  shows,
  voices,
  ttsStyles,
  initial,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState({
    show_id: initial?.show_id || shows[0]?.id || "",
    title: initial?.title || "",
    topic: initial?.topic || "",
    prompt_override: initial?.prompt_override || "",
    topic_outline: Array.isArray(initial?.topic_outline)
      ? initial.topic_outline.join("\n")
      : "",
    ad_preroll_enabled: initial?.ad_preroll_enabled ?? true,
    ad_midroll_enabled: initial?.ad_midroll_enabled ?? true,
    ad_postroll_enabled: initial?.ad_postroll_enabled ?? true,
  });
  const [speakers, setSpeakers] = useState(
    initial?.speakers?.length
      ? initial.speakers.map((s) => ({
          speaker_name: s.speaker_name || "",
          voice_id: s.voice_id || "",
          role: s.role || "host",
          persona: s.persona || "",
          style_override: s.style_override || "",
        }))
      : [
          {
            speaker_name: "",
            voice_id: voices[0]?.id || "",
            role: "host",
            persona: "",
            style_override: "",
          },
        ],
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setSpeaker = (i, k, v) =>
    setSpeakers((p) => p.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const addSpeaker = (role) =>
    setSpeakers((p) => [
      ...p,
      {
        speaker_name: "",
        voice_id: voices[0]?.id || "",
        role,
        persona: "",
        style_override: "",
      },
    ]);
  const removeSpeaker = (i) =>
    setSpeakers((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.show_id) {
      showToast("Pick a show first.");
      return;
    }
    if (!form.topic.trim()) {
      showToast("Topic is required.");
      return;
    }
    const cleaned = speakers
      .filter((s) => s.speaker_name.trim())
      .map((s, idx) => ({
        speaker_name: s.speaker_name.trim(),
        voice_id: s.voice_id ? Number(s.voice_id) : null,
        role: s.role,
        persona: s.persona || null,
        style_override: s.style_override || null,
        speaking_order: idx,
      }));
    if (cleaned.some((s) => !s.voice_id)) {
      showToast("Every speaker needs a voice.");
      return;
    }
    setSaving(true);
    const body = JSON.stringify({
      show_id: Number(form.show_id),
      title: form.title,
      topic: form.topic,
      prompt_override: form.prompt_override || null,
      topic_outline: form.topic_outline
        ? form.topic_outline
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
        : null,
      ad_preroll_enabled: form.ad_preroll_enabled,
      ad_midroll_enabled: form.ad_midroll_enabled,
      ad_postroll_enabled: form.ad_postroll_enabled,
      speakers: cleaned,
    });
    const data = initial?.id
      ? await authFetch(`/podcast/episodes/${initial.id}`, {
          method: "PUT",
          body,
        })
      : await authFetch(`/podcast/episodes`, { method: "POST", body });
    setSaving(false);
    const err = podcastErr(data, "Failed to save episode.");
    if (err) {
      showToast(err);
      return;
    }
    showToast(initial?.id ? "Episode updated." : "Episode created.", "success");
    onSaved();
  };

  return (
    <PodcastModal
      title={initial?.id ? "Edit Episode" : "New Episode"}
      onClose={onClose}
      wide
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PodcastField label="Show" required>
            <select
              className={podcastInputCls}
              value={form.show_id}
              onChange={(e) => set("show_id", e.target.value)}
              disabled={!!initial?.id}
            >
              {shows.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          </PodcastField>
          <PodcastField label="Episode title">
            <input
              className={podcastInputCls}
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Leave blank to auto-title from the script"
            />
          </PodcastField>
        </div>
        <PodcastField label="Topic" required>
          <textarea
            className={podcastInputCls}
            rows={2}
            value={form.topic}
            onChange={(e) => set("topic", e.target.value)}
            placeholder="What should this episode cover? The script is grounded in your knowledge base."
          />
        </PodcastField>
        <PodcastField label="Extra instructions (optional)">
          <textarea
            className={podcastInputCls}
            rows={2}
            value={form.prompt_override}
            onChange={(e) => set("prompt_override", e.target.value)}
          />
        </PodcastField>
        <PodcastField label="Topic outline (optional)">
          <p className="text-xs text-slate-400 mb-1.5">
            One subtopic per line. Each subtopic gets its own RAG retrieval and
            script segment — all segments are joined into one episode.
          </p>
          <textarea
            className={podcastInputCls}
            rows={6}
            value={form.topic_outline}
            onChange={(e) => set("topic_outline", e.target.value)}
            placeholder={
              "Introduction to topic\nHistorical background\nCurrent developments\nControversies or debates\nFuture implications\nReal-world applications or examples\nConclusion and key takeaways"
            }
          />
          {form.topic_outline && (
            <p className="text-xs text-slate-400 mt-1">
              {form.topic_outline.split("\n").filter((s) => s.trim()).length}{" "}
              segment
              {form.topic_outline.split("\n").filter((s) => s.trim()).length !==
              1
                ? "s"
                : ""}
            </p>
          )}
        </PodcastField>

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Hosts &amp; Guests
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => addSpeaker("host")}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-lg"
              >
                <Plus className="w-3.5 h-3.5" /> Host
              </button>
              <button
                onClick={() => addSpeaker("guest")}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-lg"
              >
                <Plus className="w-3.5 h-3.5" /> Guest
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {speakers.map((sp, i) => (
              <div
                key={i}
                className="border border-slate-200 rounded-lg p-3 space-y-2"
              >
                <div className="grid grid-cols-12 gap-2">
                  <input
                    className={`${podcastInputCls} col-span-4`}
                    value={sp.speaker_name}
                    onChange={(e) =>
                      setSpeaker(i, "speaker_name", e.target.value)
                    }
                    placeholder="Name"
                  />
                  <select
                    className={`${podcastInputCls} col-span-3`}
                    value={sp.role}
                    onChange={(e) => setSpeaker(i, "role", e.target.value)}
                  >
                    <option value="host">Host</option>
                    <option value="guest">Guest</option>
                  </select>
                  <select
                    className={`${podcastInputCls} col-span-4`}
                    value={sp.voice_id || ""}
                    onChange={(e) => setSpeaker(i, "voice_id", e.target.value)}
                  >
                    <option value="">Voice…</option>
                    {voices.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.display_name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeSpeaker(i)}
                    className="col-span-1 flex items-center justify-center text-slate-400 hover:text-red-500"
                    aria-label="Remove speaker"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <input
                    className={`${podcastInputCls} col-span-8`}
                    value={sp.persona}
                    onChange={(e) => setSpeaker(i, "persona", e.target.value)}
                    placeholder="Persona (e.g. skeptical policy analyst)"
                  />
                  <select
                    className={`${podcastInputCls} col-span-4`}
                    value={sp.style_override || ""}
                    onChange={(e) =>
                      setSpeaker(i, "style_override", e.target.value || null)
                    }
                  >
                    {(ttsStyles || PODCAST_STYLES_FALLBACK).map((s) => (
                      <option
                        key={s.value}
                        value={s.value}
                        title={s.description}
                      >
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-3 space-y-2">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
            Ad slots
          </p>
          <p className="text-xs text-slate-500">
            Toggle individual ad clips for this episode (clips are configured on
            the show).
          </p>
          {[
            { key: "ad_preroll_enabled", label: "Pre-roll" },
            { key: "ad_midroll_enabled", label: "Mid-roll" },
            { key: "ad_postroll_enabled", label: "Post-roll" },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form[key]}
                onChange={(e) => set(key, e.target.checked)}
                className="rounded border-slate-300 text-rose-600 focus:ring-rose-500"
              />
              <span className="text-xs text-slate-700">{label}</span>
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Episode
          </button>
        </div>
      </div>
    </PodcastModal>
  );
};

const PodcastVideoBridgeButton = ({ episode, facility }) => {
  const [open, setOpen] = useState(false);
  const [avatars, setAvatars] = useState(null);
  const [avatarId, setAvatarId] = useState("");
  const [creating, setCreating] = useState(false);
  const hasVideo = facility.services?.some((s) => s.service_type === "video");

  const openModal = async () => {
    setOpen(true);
    if (avatars === null) {
      const data = await authFetch(
        `/video/avatars?facility_id=${facility.facility_id}`,
      );
      const list = data.error ? [] : data.avatars || [];
      setAvatars(list);
      if (list.length) setAvatarId(String(list[0].id));
    }
  };

  const create = async () => {
    if (!avatarId) {
      showToast("Pick an avatar first.");
      return;
    }
    setCreating(true);
    const data = await authFetch(`/video/from-episode`, {
      method: "POST",
      body: JSON.stringify({
        episode_id: episode.id,
        avatar_id: Number(avatarId),
      }),
    });
    setCreating(false);
    const err = podcastErr(data, "Could not create the promo project.");
    if (err) {
      showToast(err);
      return;
    }
    const secs = Math.round((data.excerpt?.duration_ms || 0) / 1000);
    showToast(
      `Promo project created (${secs}s excerpt). Review and approve it in Video Studio.`,
      "success",
    );
    setOpen(false);
  };

  if (!hasVideo)
    return (
      <button
        disabled
        title="Video Studio isn't enabled for this facility — contact HyveAI to add it."
        className="px-3 py-1.5 text-xs text-slate-300 border border-slate-200 rounded-lg cursor-not-allowed"
      >
        Generate video
      </button>
    );
  return (
    <>
      <button
        onClick={openModal}
        className="flex items-center gap-1 px-3 py-1.5 bg-fuchsia-100 text-fuchsia-700 text-xs font-medium rounded-lg hover:bg-fuchsia-200"
      >
        <Play className="w-3.5 h-3.5" /> Generate video
      </button>
      {open && (
        <PodcastModal
          title={`Promo clip — ${episode.title || episode.topic}`}
          onClose={() => setOpen(false)}
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Builds a 60–90 second promo from this episode: the director picks
              the most compelling excerpt and the video speaks with the
              episode's own audio — no re-synthesis. You'll review the shot list
              in Video Studio before anything renders.
            </p>
            <PodcastField label="Avatar (who appears on camera)" required>
              {avatars === null ? (
                <p className="text-xs text-slate-400">Loading avatars…</p>
              ) : avatars.length === 0 ? (
                <p className="text-xs text-amber-700">
                  No avatars yet — create one in Video Studio first.
                </p>
              ) : (
                <select
                  className={podcastInputCls}
                  value={avatarId}
                  onChange={(e) => setAvatarId(e.target.value)}
                >
                  {avatars.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              )}
            </PodcastField>
            <p className="text-xs text-slate-400">
              Full-episode video (lip-synced over the whole episode) is coming
              later.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={create}
                disabled={creating || !avatarId}
                className="flex items-center gap-1.5 px-4 py-2 bg-fuchsia-600 text-white text-sm font-medium rounded-lg hover:bg-fuchsia-700 disabled:opacity-50"
              >
                {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                Create promo project
              </button>
            </div>
          </div>
        </PodcastModal>
      )}
    </>
  );
};

export const PodcastPortal = ({ facility, user }) => {
  const fid = facility.facility_id;
  const isAdmin =
    facility.is_facility_admin ||
    facility.services?.find((s) => s.service_type === "podcast")
      ?.access_role === "admin";

  const [tab, setTab] = useState("episodes");
  const [shows, setShows] = useState([]);
  const [voices, setVoices] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [episodeForm, setEpisodeForm] = useState(null); // null | {} | episode
  const [showForm, setShowForm] = useState(null);
  const [voiceForm, setVoiceForm] = useState(null);
  const [scriptView, setScriptView] = useState(null); // {episode, data}
  const [actionBusy, setActionBusy] = useState(null);
  const [shareEpisode, setShareEpisode] = useState(null);
  const [ttsStyles, setTtsStyles] = useState(PODCAST_STYLES_FALLBACK);

  // Load platform-wide styles once on mount
  useEffect(() => {
    authFetch("/podcast/styles").then((data) => {
      if (!data.error && data.styles?.length) {
        setTtsStyles([
          { value: "", label: "— None —" },
          ...data.styles.map((s) => ({
            value: s.name,
            label:
              s.name.charAt(0).toUpperCase() +
              s.name.slice(1).replace(/-/g, " "),
            description: s.description,
          })),
        ]);
      }
    });
    // eslint-disable-next-line
  }, []);

  const tabs = [
    { id: "episodes", label: "Episodes", icon: Radio },
    ...(isAdmin
      ? [
          { id: "shows", label: "Shows", icon: Mic },
          { id: "voices", label: "Voices", icon: Users },
          { id: "usage", label: "Usage", icon: BarChart3 },
        ]
      : []),
  ];

  const [costs, setCosts] = useState(null);
  const [costsLoading, setCostsLoading] = useState(false);

  const loadCosts = async () => {
    setCostsLoading(true);
    const data = await authFetch(`/podcast/costs?facility_id=${fid}`);
    setCostsLoading(false);
    if (!data.error) setCosts(data);
  };

  useEffect(() => {
    if (tab === "usage" && !costs && !costsLoading) loadCosts();
    // eslint-disable-next-line
  }, [tab]);

  const refreshEpisodes = async () => {
    const data = await authFetch(`/podcast/episodes?facility_id=${fid}`);
    if (!data.error) setEpisodes(data.episodes || []);
    return data;
  };

  const refreshShows = async () => {
    const data = await authFetch(`/podcast/shows?facility_id=${fid}`);
    if (!data.error) setShows(data.shows || []);
    return data;
  };

  const refreshVoices = async () => {
    const data = await authFetch(`/podcast/voices?facility_id=${fid}`);
    if (!data.error) setVoices(data.voices || []);
    return data;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const results = await Promise.all([
        refreshEpisodes(),
        refreshShows(),
        refreshVoices(),
      ]);
      if (results.some((r) => r && r.error)) {
        showToast("Failed to load podcast data. Please try again.");
      }
    } catch {
      showToast("Failed to load podcast data. Please try again.");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, [fid]);

  // Poll while anything is generating.
  useEffect(() => {
    // Segment (intro/outro) renders poll inside PodcastSegmentsPanel.
    const busy = episodes.some((e) => PODCAST_BUSY_STATUSES.includes(e.status));
    if (!busy) return undefined;
    const t = setInterval(() => {
      refreshEpisodes();
      refreshShows();
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [episodes, shows]);

  const runAction = async (key, endpoint, okMsg, after) => {
    setActionBusy(key);
    const data = await authFetch(endpoint, { method: "POST", body: "{}" });
    setActionBusy(null);
    const err = podcastErr(data, "Action failed. Please try again.");
    if (err) {
      showToast(err);
      return;
    }
    if (okMsg) showToast(okMsg, "success");
    if (after) after();
  };

  const archiveEpisode = async (ep) => {
    if (!window.confirm(`Archive "${ep.title || ep.topic}"?`)) return;
    const data = await authFetch(`/podcast/episodes/${ep.id}`, {
      method: "DELETE",
    });
    const err = podcastErr(data, "Failed to archive episode.");
    if (err) {
      showToast(err);
      return;
    }
    showToast("Episode archived.", "success");
    refreshEpisodes();
  };

  const archiveShow = async (s) => {
    if (!window.confirm(`Archive show "${s.title}"?`)) return;
    const data = await authFetch(`/podcast/shows/${s.id}`, {
      method: "DELETE",
    });
    const err = podcastErr(data, "Failed to archive show.");
    if (err) {
      showToast(err);
      return;
    }
    showToast("Show archived.", "success");
    refreshShows();
  };

  const deactivateVoice = async (v) => {
    if (!window.confirm(`Deactivate voice "${v.display_name}"?`)) return;
    const data = await authFetch(`/podcast/voices/${v.id}`, {
      method: "DELETE",
    });
    const err = podcastErr(data, "Failed to deactivate voice.");
    if (err) {
      showToast(err);
      return;
    }
    showToast("Voice deactivated.", "success");
    refreshVoices();
  };

  const openEpisodeEdit = async (ep) => {
    const data = await authFetch(`/podcast/episodes/${ep.id}`);
    const err = podcastErr(data, "Failed to load episode.");
    if (err) {
      showToast(err);
      return;
    }
    setEpisodeForm(data.episode);
  };

  const openScript = async (ep) => {
    const data = await authFetch(`/podcast/episodes/${ep.id}/script`);
    const err = podcastErr(data, "Failed to load script.");
    if (err) {
      showToast(err);
      return;
    }
    setScriptView({ episode: ep, data });
  };

  const showTitle = (showId) =>
    shows.find((s) => s.id === showId)?.title ||
    episodes.find((e) => e.show_id === showId)?.show_title ||
    "Show";

  const renderEpisodeCard = (ep) => {
    const busy = PODCAST_BUSY_STATUSES.includes(ep.status);
    const hasAudio = ["ready", "published"].includes(ep.status);
    const editable = PODCAST_EDITABLE_STATUSES.includes(ep.status);
    return (
      <div
        key={ep.id}
        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-all"
      >
        <div className="p-5">
          <div className="flex items-start justify-between mb-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-rose-500 font-medium mb-1">
                {ep.show_title || showTitle(ep.show_id)}
              </p>
              <h4 className="text-sm font-bold text-slate-900 truncate">
                {ep.title || ep.topic}
              </h4>
            </div>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ml-3 flex-shrink-0 ${
                PODCAST_STATUS_STYLES[ep.status] || PODCAST_STATUS_STYLES.draft
              }`}
            >
              {PODCAST_STATUS_LABELS[ep.status] || ep.status}
            </span>
          </div>
          {ep.topic && ep.title && (
            <p className="text-xs text-slate-600 leading-relaxed mb-2 line-clamp-2">
              {ep.topic}
            </p>
          )}
          {ep.total_tokens > 0 && (
            <p className="text-xs text-slate-400 mb-2">
              {ep.total_tokens.toLocaleString()} tokens (
              {ep.prompt_tokens.toLocaleString()} in /{" "}
              {ep.completion_tokens.toLocaleString()} out)
            </p>
          )}
          {busy && (
            <div className="mb-3">
              <div className="w-full bg-slate-100 rounded-full h-1.5">
                <div
                  className="bg-rose-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${ep.progress_percent || 5}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {PODCAST_STATUS_LABELS[ep.status]} ({ep.progress_percent || 0}
                %)
              </p>
            </div>
          )}
          {ep.status === "failed" && ep.error_message && (
            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-lg p-2 mb-3">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="break-words">{ep.error_message}</span>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {hasAudio && <PodcastEpisodeAudio episodeId={ep.id} />}
            {(ep.script_json || ep.script_text) && (
              <button
                onClick={() => openScript(ep)}
                className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200"
              >
                <FileText className="w-3.5 h-3.5" /> Script
              </button>
            )}
            {isAdmin && busy && (
              <button
                onClick={() =>
                  runAction(
                    `reset-${ep.id}`,
                    `/podcast/episodes/${ep.id}/reset`,
                    "Episode reset to draft.",
                    refreshEpisodes,
                  )
                }
                disabled={actionBusy === `reset-${ep.id}`}
                title="Reset stuck episode back to draft"
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-200 disabled:opacity-50"
              >
                {actionBusy === `reset-${ep.id}` ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="w-3.5 h-3.5" />
                )}
                Reset
              </button>
            )}
            {isAdmin && !busy && (
              <>
                {(editable || ep.status === "ready") && (
                  <button
                    onClick={() =>
                      runAction(
                        `gen-${ep.id}`,
                        `/podcast/episodes/${ep.id}/generate`,
                        "Generation started.",
                        refreshEpisodes,
                      )
                    }
                    disabled={actionBusy === `gen-${ep.id}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-rose-600 text-white text-xs font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50"
                  >
                    {actionBusy === `gen-${ep.id}` ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5" />
                    )}
                    {ep.script_json ? "Regenerate" : "Generate"}
                  </button>
                )}
                {ep.status === "script_ready" && ep.script_json && (
                  <button
                    onClick={() =>
                      runAction(
                        `approve-${ep.id}`,
                        `/podcast/episodes/${ep.id}/approve-script`,
                        "Script approved — audio generation queued.",
                        refreshEpisodes,
                      )
                    }
                    disabled={actionBusy === `approve-${ep.id}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {actionBusy === `approve-${ep.id}` ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CheckCircle className="w-3.5 h-3.5" />
                    )}
                    Approve
                  </button>
                )}
                {ep.status === "script_ready" && ep.script_json && (
                  <PodcastPreviewButton episodeId={ep.id} />
                )}
                {ep.status === "published" &&
                  shows.find((s) => s.id === ep.show_id)?.feed_token && (
                    <a
                      href={`${AUTH_URL}/podcast/feed/${
                        shows.find((s) => s.id === ep.show_id).feed_token
                      }/episodes/${ep.id}.vtt`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 underline"
                    >
                      Transcript
                    </a>
                  )}
                {ep.script_json && ["ready", "failed"].includes(ep.status) && (
                  <button
                    onClick={() =>
                      runAction(
                        `regen-${ep.id}`,
                        `/podcast/episodes/${ep.id}/regenerate-audio`,
                        "Audio regeneration started.",
                        refreshEpisodes,
                      )
                    }
                    disabled={actionBusy === `regen-${ep.id}`}
                    title="Rebuilds the audio using the show's currently APPROVED intro and outro versions and current assets — the episode will be re-stamped with those versions."
                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200 disabled:opacity-50"
                  >
                    <Volume2 className="w-3.5 h-3.5" /> Re-voice
                  </button>
                )}
                {isAdmin && ["ready", "published"].includes(ep.status) && (
                  <PodcastVideoBridgeButton episode={ep} facility={facility} />
                )}
                {ep.status === "ready" && (
                  <button
                    onClick={() =>
                      runAction(
                        `pub-${ep.id}`,
                        `/podcast/episodes/${ep.id}/publish`,
                        "Episode published.",
                        refreshEpisodes,
                      )
                    }
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700"
                  >
                    <Check className="w-3.5 h-3.5" /> Publish
                  </button>
                )}
                {ep.status === "published" &&
                  shows.find((s) => s.id === ep.show_id)?.feed_token && (
                    <button
                      onClick={() =>
                        setShareEpisode({
                          episode: ep,
                          feedToken: shows.find((s) => s.id === ep.show_id)
                            .feed_token,
                        })
                      }
                      title="Publish this episode to connected social accounts"
                      className="flex items-center gap-1 px-3 py-1.5 bg-sky-600 text-white text-xs font-medium rounded-lg hover:bg-sky-700"
                    >
                      <Share2 className="w-3.5 h-3.5" /> Share
                    </button>
                  )}
                {ep.status === "published" && (
                  <button
                    onClick={() =>
                      runAction(
                        `unpub-${ep.id}`,
                        `/podcast/episodes/${ep.id}/unpublish`,
                        "Episode unpublished.",
                        refreshEpisodes,
                      )
                    }
                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200"
                  >
                    Unpublish
                  </button>
                )}
                {editable && (
                  <button
                    onClick={() => openEpisodeEdit(ep)}
                    className="flex items-center gap-1 px-2 py-1.5 text-slate-500 hover:text-rose-600 text-xs"
                    aria-label="Edit episode"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => archiveEpisode(ep)}
                  className="flex items-center gap-1 px-2 py-1.5 text-slate-400 hover:text-red-500 text-xs"
                  aria-label="Archive episode"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
          {hasAudio && <PodcastMixLanes manifest={ep.mix_manifest} />}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-1 bg-white rounded-lg shadow-sm border border-slate-200 p-1 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              tab === t.id
                ? "bg-rose-600 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <t.icon className="w-4 h-4 mr-2" />
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors disabled:opacity-50 flex-shrink-0"
          title="Refresh"
          aria-label="Refresh podcast data"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-sm text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading podcast data...
        </div>
      ) : (
        <>
          {tab === "episodes" && (
            <div className="space-y-4">
              {isAdmin && (
                <div className="flex justify-end">
                  <button
                    onClick={() => setEpisodeForm({})}
                    disabled={shows.length === 0 || voices.length === 0}
                    className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50"
                    title={
                      shows.length === 0 || voices.length === 0
                        ? "Create a show and at least one voice first"
                        : undefined
                    }
                  >
                    <Plus className="w-4 h-4" /> New Episode
                  </button>
                </div>
              )}
              {episodes.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Headphones className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">
                    No episodes available yet.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {episodes.map(renderEpisodeCard)}
                </div>
              )}
            </div>
          )}

          {tab === "shows" && isAdmin && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowForm({})}
                  className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700"
                >
                  <Plus className="w-4 h-4" /> New Show
                </button>
              </div>
              {shows.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Mic className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">
                    No shows yet. Create one to get started.
                  </p>
                </div>
              ) : (
                shows.map((s) => {
                  return (
                    <div
                      key={s.id}
                      className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                    >
                      <div className="px-6 py-4 bg-gradient-to-r from-rose-50 to-white border-b border-slate-100 flex items-center justify-between">
                        <div className="min-w-0">
                          <h3 className="text-lg font-bold text-slate-900 truncate">
                            {s.title}
                          </h3>
                          {s.description && (
                            <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">
                              {s.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                            {
                              PODCAST_PROVIDERS.find(
                                (p) => p.id === s.tts_provider,
                              )?.label
                            }
                          </span>
                          <button
                            onClick={() => setShowForm(s)}
                            className="text-slate-400 hover:text-rose-600"
                            aria-label="Edit show"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => archiveShow(s)}
                            className="text-slate-400 hover:text-red-500"
                            aria-label="Archive show"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="px-6 py-4">
                        {/* Copy pending human review (Hard Rule 8) */}
                        <p className="text-xs text-slate-500">
                          Intro &amp; outro are versioned per show — edit the
                          show to write, preview, and approve them. Episodes
                          keep the version they were rendered with.
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {tab === "voices" && isAdmin && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={() => setVoiceForm({})}
                  className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700"
                >
                  <Plus className="w-4 h-4" /> New Voice
                </button>
              </div>
              {voices.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">
                    No voices yet. Add one to start producing episodes.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {voices.map((v) => (
                    <div
                      key={v.id}
                      className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                            {(v.display_name || "V")
                              .split(" ")
                              .map((w) => w[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-semibold text-slate-900 truncate">
                              {v.display_name}
                            </h4>
                            <p className="text-xs text-slate-400 truncate">
                              {v.provider_voice_id}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          <button
                            onClick={() => setVoiceForm(v)}
                            className="text-slate-400 hover:text-rose-600"
                            aria-label="Edit voice"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deactivateVoice(v)}
                            className="text-slate-400 hover:text-red-500"
                            aria-label="Deactivate voice"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-2">
                        <span className="px-1.5 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                          {
                            PODCAST_PROVIDERS.find((p) => p.id === v.provider)
                              ?.label
                          }
                        </span>
                        {v.gender && (
                          <span className="px-1.5 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                            {v.gender}
                          </span>
                        )}
                        {v.default_style && (
                          <span className="px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                            {v.default_style}
                          </span>
                        )}
                        {v.is_cloned && (
                          <span className="px-1.5 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                            Cloned · consent on file
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "usage" && isAdmin && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  Monthly generation usage. Budgets reset on the 1st.
                </p>
                <button
                  onClick={loadCosts}
                  disabled={costsLoading}
                  className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50"
                >
                  Refresh
                </button>
              </div>
              {costsLoading || !costs ? (
                <div className="text-center py-12 text-sm text-slate-400">
                  Loading usage…
                </div>
              ) : (
                <>
                  {costs.quota?.exceeded && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                      {costs.quota.exceeded}. Generation is paused until the
                      budget resets or is raised.
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      {
                        label: "Script generation (LLM tokens)",
                        used: costs.quota?.tokens_used || 0,
                        budget: costs.quota?.token_budget || 0,
                      },
                      {
                        label: "Audio generation (TTS characters)",
                        used: costs.quota?.chars_used || 0,
                        budget: costs.quota?.char_budget || 0,
                      },
                    ].map((q) => {
                      const pct = q.budget
                        ? Math.min(100, Math.round((q.used / q.budget) * 100))
                        : 0;
                      return (
                        <div
                          key={q.label}
                          className="bg-white rounded-xl border border-slate-200 p-5"
                        >
                          <p className="text-xs text-slate-500 mb-1">
                            {q.label}
                          </p>
                          <p className="text-xl font-bold text-slate-900">
                            {q.used.toLocaleString()}
                            <span className="text-sm font-normal text-slate-400">
                              {q.budget
                                ? ` / ${q.budget.toLocaleString()} this month`
                                : " this month"}
                            </span>
                          </p>
                          {q.budget > 0 ? (
                            <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  pct >= 100
                                    ? "bg-red-500"
                                    : pct >= 80
                                      ? "bg-amber-400"
                                      : "bg-rose-500"
                                }`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          ) : (
                            <p className="mt-2 text-xs text-slate-400">
                              No monthly budget set — unlimited.
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-left text-xs text-slate-500">
                          <th className="px-4 py-2 font-medium">Month</th>
                          <th className="px-4 py-2 font-medium">Show</th>
                          <th className="px-4 py-2 font-medium text-right">
                            LLM tokens
                          </th>
                          <th className="px-4 py-2 font-medium text-right">
                            Azure chars
                          </th>
                          <th className="px-4 py-2 font-medium text-right">
                            ElevenLabs chars
                          </th>
                          <th className="px-4 py-2 font-medium text-right">
                            Piper chars
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(costs.months || []).length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="px-4 py-8 text-center text-slate-400"
                            >
                              No usage recorded yet.
                            </td>
                          </tr>
                        ) : (
                          costs.months.map((m, i) => (
                            <tr
                              key={i}
                              className="border-t border-slate-100 text-slate-700"
                            >
                              <td className="px-4 py-2">
                                {m.month?.slice(0, 7)}
                              </td>
                              <td className="px-4 py-2">
                                {shows.find((s) => s.id === m.show_id)?.title ||
                                  `Show ${m.show_id}`}
                              </td>
                              <td className="px-4 py-2 text-right">
                                {(m.total_tokens || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-right">
                                {(m.azure_chars || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-right">
                                {(m.elevenlabs_chars || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-right">
                                {(m.piper_chars || 0).toLocaleString()}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {episodeForm !== null && (
        <PodcastEpisodeForm
          shows={shows}
          voices={voices}
          ttsStyles={ttsStyles}
          initial={episodeForm.id ? episodeForm : null}
          onClose={() => setEpisodeForm(null)}
          onSaved={() => {
            setEpisodeForm(null);
            refreshEpisodes();
          }}
        />
      )}
      {showForm !== null && (
        <PodcastShowForm
          facilityId={fid}
          voices={voices}
          episodes={episodes}
          initial={showForm.id ? showForm : null}
          onClose={() => setShowForm(null)}
          onSaved={() => {
            setShowForm(null);
            refreshShows();
          }}
        />
      )}
      {voiceForm !== null && (
        <PodcastVoiceForm
          facilityId={fid}
          ttsStyles={ttsStyles}
          initial={voiceForm.id ? voiceForm : null}
          onClose={() => setVoiceForm(null)}
          onSaved={() => {
            setVoiceForm(null);
            refreshVoices();
          }}
        />
      )}
      {scriptView && (
        <PodcastScriptModal
          episode={scriptView.episode}
          data={scriptView.data}
          isAdmin={isAdmin}
          onClose={() => setScriptView(null)}
          onChanged={refreshEpisodes}
        />
      )}
      {shareEpisode && (
        <SocialShareDialog
          facility={facility}
          content={{
            content_id: `podcast-episode-${shareEpisode.episode.id}`,
            kind: "link",
            title:
              shareEpisode.episode.title || shareEpisode.episode.topic || "",
            body: `New episode: ${
              shareEpisode.episode.title || shareEpisode.episode.topic || ""
            }`,
            link_url: episodePublicAudioUrl(
              shareEpisode.feedToken,
              shareEpisode.episode.id,
            ),
          }}
          defaultBody={`New episode: ${
            shareEpisode.episode.title || shareEpisode.episode.topic || ""
          }`}
          onClose={() => setShareEpisode(null)}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// ADMIN: RAG SERVICE CONFIG
// ═══════════════════════════════════════════════════════════════════
